// @noflow

import * as React from "react";

import { FormattedMessage } from "react-intl";
import { History, Location } from "history";
import { match } from "react-router-dom";

import { LINE_GROUP_TAGS, INSIGHT_TYPES, LINE_TAGS_ACCESS } from "../../lib/ManufacturingTags.generated";
import * as BusinessLogic from "../../lib/BusinessLogic";
import { shiftNumber } from "../../lib/Util";
import { getBackend } from "../../lib/backend/Backend2";
import { NavigationBarWithDropdown, type HandleViewChangeEvent } from "../NavigationBar";
import DashboardStandalone from "../DashboardStandalone";
import { TOOLS_VIEW_TYPES, redirectSelectedView } from "../ViewsCommon";
import InsightsViewToolSetup from "./InsightsViewToolSetup";
import InsightsView from "./InsightsView";
import { GanttChartSource } from "./PlanningTable2/reducers/properties";
import * as Auth from "../../lib/Auth";
import ToolManagement from "./ToolManagement";
import { Toolbar, ToolbarGroup, ToolbarItem } from "../Toolbar";
import { classNames } from "../../lib/Util";

const VIEW_TYPES = TOOLS_VIEW_TYPES;

type ToolsViewParams = {
    id?: string;
    type: string;
    title?: string;
    title_code?: string;
    dashboard_uuid?: string;
};

type Props = {
    history: History;
    match: match<{ uuid?: string; selected_view?: string }>;
    location: Location;
    plant_uuid: string;
    line_group_uuid: string;
};

type State = {
    views: ToolsViewParams[];
    selected_view: string;
    tool_change_exclude_line_uuids: string[];
};

class ToolsView extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            views: [],
            selected_view: VIEW_TYPES.tool_change.id,
            tool_change_exclude_line_uuids: [],
        };
    }

    async componentDidMount() {
        await BusinessLogic.waitForManufacturingAsync();
        this.loadViews();
    }

    async componentDidUpdate(prev_props: Props) {
        if (
            prev_props.plant_uuid !== this.props.plant_uuid ||
            prev_props.line_group_uuid !== this.props.line_group_uuid ||
            (this.props.match.params.selected_view &&
                this.props.match.params.selected_view !== this.state.selected_view)
        ) {
            await BusinessLogic.waitForManufacturingAsync();
            this.loadViews();
        }
    }

    async loadViews() {
        const line_group = BusinessLogic.getLineGroupForUser(this.props.line_group_uuid);
        if (line_group === null) {
            return;
        }

        // create views
        const views: ToolsViewParams[] = [];
        const tool_change_exclude_line_uuids: string[] = [];
        try {
            const { lines } = await getBackend().manufacturing.getLines({
                line_group_uuids: [this.props.line_group_uuid],
            });
            for (const line of lines) {
                if (LINE_TAGS_ACCESS.skip_in_tool_changes_view(line.tags)) {
                    tool_change_exclude_line_uuids.push(line.uuid);
                }
            }
        } catch (error: unknown) {
            console.error("Error loading lines", error);
        }

        const is_ticketing_user = Auth.isInRoles([Auth.ROLE_TICKET_USER, Auth.ROLE_TICKET_ADMIN]);
        if (!is_ticketing_user) {
            views.push({
                id: VIEW_TYPES.tool_change.id,
                type: VIEW_TYPES.tool_change.id,
                title: VIEW_TYPES.tool_change.title,
                title_code: VIEW_TYPES.tool_change.title_code,
            });
        } else {
            views.push({
                id: VIEW_TYPES.tool_change_new.id,
                type: VIEW_TYPES.tool_change_new.id,
                title: VIEW_TYPES.tool_change_new.title,
                title_code: VIEW_TYPES.tool_change_new.title_code,
            });
        }

        try {
            // get line-group tool-change gantt
            const res1 = await getBackend().dashboards.searchDashboards({
                active: true,
                tags: {
                    line_group: this.props.line_group_uuid,
                    type: "man",
                    subtype: "plant-gantt-tool-setup",
                },
            });
            // as a backup show plant-level tool-change gantt
            const res2 = await getBackend().dashboards.searchDashboards({
                active: true,
                tags: {
                    plant: this.props.plant_uuid,
                    type: "man",
                    subtype: "plant-gantt-tool-setup",
                },
            });

            // go over dashboards and identify relevant ones
            const dashboards_toolsetup = [...res1.dashboards, ...res2.dashboards];
            if (dashboards_toolsetup.length > 0) {
                const gantt_dashboard = dashboards_toolsetup[0];
                views.push({
                    id: VIEW_TYPES.gantt_tools.id,
                    type: VIEW_TYPES.gantt_tools.id,
                    title: VIEW_TYPES.gantt_tools.title,
                    title_code: VIEW_TYPES.gantt_tools.title_code,
                    dashboard_uuid: gantt_dashboard.uuid,
                });
            }
        } catch (error: unknown) {
            console.error("Error loading tool-change gantt", error);
        }

        views.push({
            id: VIEW_TYPES.tool_management.id,
            type: VIEW_TYPES.tool_management.id,
            title: VIEW_TYPES.tool_management.title,
            title_code: VIEW_TYPES.tool_management.title_code,
        });

        // assing appropriate view
        const url_view = this.props.match.params.selected_view;
        let selected_view = "";
        if (url_view) {
            // we have view selected by the URL, check if even possible
            if (views.find(view => view.id === url_view)) {
                // all is good, mark it selected
                selected_view = url_view;
            } else {
                // url_view no longer available, select the top one
                selected_view = views.length > 0 && views[0].id ? views[0].id : "";
            }
        } else {
            // no view indicated in url, select the top one
            selected_view = views.length > 0 && views[0].id ? views[0].id : "";
        }
        this.setState({
            views,
            selected_view,
            tool_change_exclude_line_uuids,
        });
        this.props.history.push(`/manufacturing/tools/${selected_view}`);
    }

    handleViewChange = (event: HandleViewChangeEvent) => {
        // read new value
        const selected_view = (event.currentTarget as HTMLButtonElement)?.value ?? "";
        // find new id
        this.setState({ selected_view });
        redirectSelectedView(selected_view, this.props.history);
    };

    /////////////////////////////////////////
    // Tool Change
    renderToolChangeFilter() {
        return (
            <ToolbarGroup align="end" grow>
                <ToolbarItem>
                    <button className="btn btn-primary" onClick={this.onDownloadHTML}>
                        <FormattedMessage id="ShiftDetails.export_for_print" defaultMessage="Export for print" />
                    </button>
                </ToolbarItem>
                <ToolbarItem>
                    <button className="btn btn-primary" onClick={this.handleExportToXlsx}>
                        <FormattedMessage
                            id="Manufacturing.MicroplanSummary.export_to_xlsx"
                            defaultMessage="Export To XLSX"
                        />
                    </button>
                </ToolbarItem>
            </ToolbarGroup>
        );
    }

    /////////////////////////////////////////
    // Tools View
    renderFilter(view: ToolsViewParams) {
        if (view.type === VIEW_TYPES.tool_change.id) {
            return this.renderToolChangeFilter();
        }

        return null;
    }

    renderContent(view: ToolsViewParams) {
        if (view) {
            if (view.type === VIEW_TYPES.tool_change.id) {
                const insight_types = [INSIGHT_TYPES.tool_setup, INSIGHT_TYPES.tool_overlap];
                const shift_start = shiftNumber(new Date());
                const diff = new Date().getTime() - shift_start.ts_start;
                return (
                    <InsightsView
                        history={this.props.history}
                        plant_uuid={this.props.plant_uuid}
                        line_group_uuid={this.props.line_group_uuid}
                        exclude_line_uuids={this.state.tool_change_exclude_line_uuids}
                        insight_types={insight_types}
                        from={new Date().getTime() - diff}
                    />
                );
            } else if (view.type === VIEW_TYPES.tool_change_new.id) {
                const shift_start = shiftNumber(new Date()).ts_start;
                const diff = new Date().getTime() - shift_start;
                return (
                    <InsightsViewToolSetup
                        history={this.props.history}
                        match={this.props.match}
                        plant_uuid={this.props.plant_uuid}
                        line_group_uuid={this.props.line_group_uuid}
                        exclude_line_uuids={this.state.tool_change_exclude_line_uuids}
                        from={new Date().getTime() - diff}
                    />
                );
            } else if (view.type === VIEW_TYPES.tool_management.id) {
                return (
                    <ToolManagement plant_uuid={this.props.plant_uuid} line_group_uuid={this.props.line_group_uuid} />
                );
            } else if (view.dashboard_uuid) {
                return (
                    <DashboardStandalone
                        history={this.props.history}
                        match={this.props.match}
                        uuid={view.dashboard_uuid}
                        asPublic={false}
                        show_filter_bar={false}
                        source_type={GanttChartSource.tool_changes}
                    />
                );
            } else {
                return <div></div>;
            }
        }
        // nothing valid selected
        return <div></div>;
    }

    onDownloadHTML = () => {
        const event = new Event("export-html-insights");
        const el = document.querySelector("#insights-list");

        if (el instanceof HTMLElement) {
            el.dispatchEvent(event);
        }
    };

    handleExportToXlsx = () => {
        const event = new CustomEvent("export-xlsx-insights", { detail: INSIGHT_TYPES.tool_setup });
        const el = document.querySelector("#insights-list");

        if (el instanceof HTMLElement) {
            el.dispatchEvent(event);
        }
    };

    getViewsContentStyle = (view?: ToolsViewParams) => {
        if (!view) return {};

        if (view.type === TOOLS_VIEW_TYPES.gantt_tools.id) {
            return {
                paddingTop: "75px",
            };
        }

        return {};
    };

    shouldHideFilter = (view?: ToolsViewParams): boolean => {
        return (
            view != undefined &&
            [
                TOOLS_VIEW_TYPES.gantt_tools.id,
                TOOLS_VIEW_TYPES.tool_change_new.id,
                TOOLS_VIEW_TYPES.tool_management.id,
            ].includes(view.type)
        );
    };

    renderContainerFilter = (view?: ToolsViewParams) => {
        const hide_filter_bar = this.shouldHideFilter(view);
        if (hide_filter_bar || view === undefined) {
            return null;
        }

        return <Toolbar className="views-filter-bar views_always_top">{this.renderFilter(view)}</Toolbar>;
    };

    getViewId = (view?: ToolsViewParams): string => {
        if (view) {
            if (view.id) {
                return view.id;
            }
        }
        return "";
    };

    render() {
        const view = this.state.views.find(view => view.id === this.state.selected_view);
        if (!view) {
            return null;
        }

        const hide_filter = this.shouldHideFilter(view);
        return (
            <React.Fragment>
                <NavigationBarWithDropdown
                    handleViewChange={this.handleViewChange}
                    nav="views"
                    nav_id="tools"
                    location={this.props.location}
                    line_group_uuid={this.props.line_group_uuid}
                    plant_uuid={this.props.plant_uuid}
                />
                {this.renderContainerFilter(view)}
                <div
                    className={classNames("views-content", this.getViewId(view), {
                        "hide-filter": hide_filter,
                    })}
                    style={this.getViewsContentStyle(view)}
                >
                    {view && this.renderContent(view)}
                </div>
            </React.Fragment>
        );
    }
}

export default ToolsView;
